// GA4 gated by cookie consent. Never sends PII (names, emails, phones, addresses, photos).
const GA_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
const CONSENT_KEY = "rm_cookie_consent";
const PURCHASE_PREFIX = "rm_ga_purchase_";
const PII_KEYS = ["email", "phone", "name", "customer_name", "address", "pet_name", "photo_base64", "notes"];

let lastPagePath = null;

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function hasAnalyticsConsent() {
  const c = readConsent();
  return !!(c && c.analytics === true);
}

export function saveConsent(nextValue) {
  const payload = { ...(readConsent() || {}), ...nextValue, ts: Date.now() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch (e) {
    /* ignore */
  }
  applyConsent();
  window.dispatchEvent(new CustomEvent("rm:consent-changed", { detail: payload }));
  return payload;
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent("rm:open-cookie-prefs"));
}

function currentPath() {
  return window.location.pathname;
}

function loadGtag() {
  window[`ga-disable-${GA_ID}`] = false;
  if (window.__ga_loaded__) {
    window.gtag("consent", "update", { analytics_storage: "granted" });
  } else {
    window.__ga_loaded__ = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: false,
      page_location: `${window.location.origin}${currentPath()}`,
    });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
  }
  lastPagePath = null;
  trackPageView(currentPath());
}

function clearGaCookies() {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", host, `.${host}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (name.startsWith("_ga") || name === "_gid" || name === "_gat") {
      domains.forEach((d) => {
        const domainPart = d ? `; domain=${d}` : "";
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
      });
    }
  });
}

function unloadGtag() {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
  window[`ga-disable-${GA_ID}`] = true;
  lastPagePath = null;
  clearGaCookies();
}

export function applyConsent() {
  if (!GA_ID) return;
  if (hasAnalyticsConsent()) loadGtag();
  else unloadGtag();
}

function track(eventName, params = {}) {
  if (!GA_ID || !hasAnalyticsConsent() || typeof window.gtag !== "function") return;
  const cleaned = { ...params };
  PII_KEYS.forEach((k) => delete cleaned[k]);
  try {
    window.gtag("event", eventName, cleaned);
  } catch (e) {
    /* ignore */
  }
}

// Query strings are stripped so session ids never reach GA.
export function trackPageView(path) {
  if (path === lastPagePath) return;
  if (!GA_ID || !hasAnalyticsConsent() || typeof window.gtag !== "function") return;
  lastPagePath = path;
  const pageLocation = `${window.location.origin}${path}`;
  window.gtag("set", { page_location: pageLocation, page_path: path });
  track("page_view", {
    page_path: path,
    page_location: pageLocation,
    page_title: document.title,
  });
}

export function trackHeroCta() {
  track("select_promotion", { promotion_name: "hero_pedir_mi_retrato", promotion_id: "hero_cta" });
}

export function trackViewStyle(styleId, styleName) {
  track("view_item", {
    currency: "EUR",
    value: 29.5,
    items: [{ item_id: styleId, item_name: styleName, item_category: "retrato", price: 29.5 }],
  });
}

export function trackBeginCheckout(styleId, styleName, priceEur = 29.5) {
  track("begin_checkout", {
    currency: "EUR",
    value: priceEur,
    items: [{ item_id: styleId, item_name: styleName, item_category: "retrato", price: priceEur, quantity: 1 }],
  });
}

export function trackPhotoUpload(styleId) {
  track("upload_photo", { item_id: styleId });
}

export function trackAddShippingInfo(styleId, priceEur = 29.5) {
  track("add_shipping_info", {
    currency: "EUR",
    value: priceEur,
    items: [{ item_id: styleId, quantity: 1, price: priceEur }],
  });
}

export function trackInitiatePayment(styleId, priceEur = 29.5) {
  track("add_payment_info", {
    currency: "EUR",
    value: priceEur,
    payment_type: "card",
    items: [{ item_id: styleId, quantity: 1, price: priceEur }],
  });
}

// Fires once per order (dedup by order id in localStorage).
export function trackPurchase({ transactionId, amount, currency = "EUR", styleId, styleName }) {
  if (!transactionId || !hasAnalyticsConsent()) return;
  const key = `${PURCHASE_PREFIX}${transactionId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, String(Date.now()));
  } catch (e) {
    /* ignore */
  }
  const value = Number(amount) || 0;
  track("purchase", {
    transaction_id: transactionId,
    value,
    currency: (currency || "EUR").toUpperCase(),
    shipping: 0,
    tax: 0,
    items: [
      {
        item_id: styleId || "retrato",
        item_name: styleName,
        item_category: "retrato",
        price: value,
        quantity: 1,
      },
    ],
  });
}
